import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'

import LocalStorageButtons from '@components/LocalStorageButtons'
import FilterFromToDate from '@components/toolbar/FilterFromToDate'
import FilterGridContainer from '@components/toolbar/FilterGridContainer'
import FilterGridItem from '@components/toolbar/FilterGridItem'
import FilterKpi from '@components/toolbar/FilterKpi'
import { FilterMyList } from '@components/toolbar/FilterMyList'
import { FilterSelectGebied } from '@components/toolbar/FilterSelectGebied'
import { FilterSelectObject } from '@components/toolbar/FilterSelectObject'
import type { FilterOption, GroupedFilterOption } from '@models/Filter'
import type { Gebied } from '@models/Gebied'
import { defaultIsOptionEqualToValue } from '@util/filter'

import { useAuthenticatedUser } from '../../../auth/hooks/useAuthenticatedUser'
import { useLocalFilters } from '../../../core/hooks/useLocalFilters'
import {
  SubprocesStatusAansluiting,
  SubprocesTypesAansluiting,
} from '../../../entity/subprocessen/types/Subproces'
import { StyledIconButton } from '../../../theme/components/StyledIconButton'
import { queryKey } from '../hooks/useSubprocessenSearch'
import type { SubprocessenSearchFilters as Filters } from '../types/SubprocessenSearch'

const kpiTooltips = {
  red: 'Laat alleen de verlopen subprocessen zien',
  amber: 'Laat bijna verlopen subprocessen zien',
  green: 'Laat alle subprocessen zien die niet (bijna) verlopen zijn',
  grey: 'Laat alle subprocessen zien',
}

interface Props {
  onChange: (filters: Filters) => void
  filters: Filters
  initialFilters: Filters
  onRefresh: () => void
  onReset: () => void
  filterOptions: {
    actiehouders: GroupedFilterOption[]
    gebieden: Gebied[]
    labels: FilterOption[]
  }
}

export const SubprocessenFilters = ({
  onChange,
  filters,
  initialFilters,
  onRefresh,
  onReset,
  filterOptions: { actiehouders, gebieden, labels: labelOptions },
}: Props) => {
  const { combi } = useAuthenticatedUser()

  const { localFilters, handleFilterChange, handleZoeken, handleReset } =
    useLocalFilters<Filters>({
      filters,
      initialFilters,
      onChange,
      onRefresh,
      onReset,
    })

  const {
    gebied,
    type,
    assignedTo,
    fromDate,
    toDate,
    fromGeplandeUitvoeringsdatum,
    toGeplandeUitvoeringsdatum,
    status,
    myList,
    kpi,
    labels,
  } = localFilters

  return (
    <FilterGridContainer>
      <FilterGridItem>
        <FilterSelectObject
          multiple
          options={Object.entries(SubprocesTypesAansluiting).map(
            ([code, label]) => ({
              code,
              label,
            }),
          )}
          label="Type subproces"
          filterKey="type"
          value={type}
          onFilterChange={handleFilterChange}
          isOptionEqualToValue={defaultIsOptionEqualToValue}
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectObject
          multiple
          options={Object.entries(SubprocesStatusAansluiting).map(
            ([code, label]) => ({
              code,
              label,
            }),
          )}
          isOptionEqualToValue={defaultIsOptionEqualToValue}
          label="Status subproces"
          filterKey="status"
          value={status}
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectGebied
          options={gebieden}
          value={gebied}
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      {combi && (
        <FilterGridItem>
          <FilterSelectObject
            multiple
            options={actiehouders}
            label="Toegewezen aan"
            filterKey="assignedTo"
            value={assignedTo}
            onFilterChange={handleFilterChange}
          />
        </FilterGridItem>
      )}

      {labelOptions.length > 0 && (
        <FilterGridItem>
          <FilterSelectObject
            multiple
            options={labelOptions}
            label="Labels"
            filterKey="labels"
            value={labels}
            onFilterChange={handleFilterChange}
            isOptionEqualToValue={defaultIsOptionEqualToValue}
          />
        </FilterGridItem>
      )}

      <FilterGridItem minColumns="double">
        <FilterFromToDate
          fromDate={fromGeplandeUitvoeringsdatum}
          toDate={toGeplandeUitvoeringsdatum}
          onFilterChange={handleFilterChange}
          label="Project gepland"
          prop="GeplandeUitvoeringsdatum"
        />
      </FilterGridItem>

      <FilterGridItem minColumns="double">
        <FilterFromToDate
          onFilterChange={handleFilterChange}
          label="Opleverdatum"
          fromDate={fromDate}
          toDate={toDate}
        />
      </FilterGridItem>

      <FilterGridItem
        minColumns="full"
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <FilterKpi
          onFilterChange={handleFilterChange}
          kpi={kpi}
          tooltips={kpiTooltips}
        />
        <StyledIconButton
          icon={<CloseIcon />}
          title="Reset filters"
          onClick={handleReset}
        />
        <StyledIconButton
          icon={<SearchIcon />}
          title="Zoeken"
          onClick={handleZoeken}
        />

        <FilterMyList myList={myList} onFilterChange={handleFilterChange} />

        <LocalStorageButtons
          name={queryKey}
          storable={localFilters}
          onClickLoad={handleFilterChange}
        />
      </FilterGridItem>
    </FilterGridContainer>
  )
}
